'use client';

import { useEffect, useRef, useState, useCallback } from 'react';

const RECOVERABLE_STATUSES = ['pending', 'setting_up', 'crawling', 'processing_content', 'analyzing', 'training', 'generating_prompt', 'finalizing'];
const STALE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

export interface AgentSetupStatus {
  setupStatus: string;
  setupStep: number;
  setupError: string | null;
  stepLabel: string;
  totalSteps: number;
  isComplete: boolean;
  isError: boolean;
}

export interface UseAgentSetupStatusOptions {
  agentId: string | null;
  enabled?: boolean;
  pollIntervalMs?: number;
}

export function useAgentSetupStatus({
  agentId,
  enabled = true,
  pollIntervalMs = 3000,
}: UseAgentSetupStatusOptions) {
  const [status, setStatus] = useState<AgentSetupStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const recoveryTriggeredRef = useRef(false);
  const statusFirstSeenRef = useRef<{ status: string; at: number } | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!agentId) return null;

    try {
      const res = await fetch(`/api/agents/${agentId}/setup-status`);
      if (!res.ok) return null;

      const data = await res.json();
      setStatus({
        setupStatus: data.setupStatus || 'ready',
        setupStep: data.setupStep ?? 0,
        setupError: data.setupError ?? null,
        stepLabel: data.stepLabel || 'Preparing...',
        totalSteps: data.totalSteps ?? 6,
        isComplete: data.isComplete ?? false,
        isError: data.isError ?? false,
      });
      return data;
    } catch {
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [agentId]);

  const triggerRunSetup = useCallback(() => {
    if (!agentId) return;
    fetch(`/api/agents/${agentId}/run-setup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    }).catch(() => {});
  }, [agentId]);

  // Poll for status updates; trigger recovery if status is stale (intermediate for 5+ min)
  useEffect(() => {
    if (!agentId || !enabled) return;

    let intervalId: ReturnType<typeof setInterval> | null = null;

    const poll = () => {
      fetchStatus().then((data) => {
        if (data?.isComplete || data?.isError) {
          if (intervalId) clearInterval(intervalId);
          return;
        }
        // Stale recovery: intermediate status unchanged for 5+ min
        if (data && !recoveryTriggeredRef.current && RECOVERABLE_STATUSES.includes(data.setupStatus)) {
          const now = Date.now();
          const prev = statusFirstSeenRef.current;
          if (prev?.status === data.setupStatus) {
            if (now - prev.at > STALE_THRESHOLD_MS) {
              recoveryTriggeredRef.current = true;
              statusFirstSeenRef.current = null;
              triggerRunSetup();
            }
          } else {
            statusFirstSeenRef.current = { status: data.setupStatus, at: now };
          }
        }
      });
    };

    poll();
    intervalId = setInterval(poll, pollIntervalMs);

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [agentId, enabled, pollIntervalMs, fetchStatus, triggerRunSetup]);

  const retry = useCallback(async () => {
    if (!agentId) return;

    recoveryTriggeredRef.current = false;
    statusFirstSeenRef.current = null;
    await fetch(`/api/agents/${agentId}/run-setup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });

    setStatus((prev) =>
      prev
        ? {
            ...prev,
            setupStatus: 'setting_up',
            setupStep: 0,
            setupError: null,
            isComplete: false,
            isError: false,
            stepLabel: 'Preparing...',
          }
        : null
    );

    fetchStatus();
  }, [agentId, fetchStatus]);

  return {
    status,
    isLoading,
    isComplete: status?.isComplete ?? false,
    isError: status?.isError ?? false,
    retry,
    refetch: fetchStatus,
  };
}
